import React from "react";
import findStep from "./findStep";
import PixPaymentImage from "./components/PixPaymentImage";
import useBotControlStore from "../../../storeModules/useBotControlStore";
import useConversacaoStore from "../../../storeModules/useConversacaoStore";
import useUserStore from "../../../storeModules/useUserStore";
import useConsorcioStore from "../../../storeModules/useConsorcioStore";
import usePaymentStore from "../../../storeModules/usePaymentStore";
import useInitialConfigStore from "../../../storeModules/useInitialConfigStore";
import { recuperarConversacao, recuperarMensagens } from "../../../api/requestsFunctions/conversacao";
import { CategoriasEnum, categoryNames } from "../../../utils/constants";
import hasUrlCoupon from "../../../utils/hasUrlCoupon";
import useInsuranceStore from "../../../storeModules/useInsuranceStore";
import {
  restoreResumeStorage,
  clearResumeExtraStorage,
} from "../../../utils/resumeStorage";
import { hasSessionConfigMismatch } from "../../../utils/sessionConfigStorage";
import { removeLoadingMessage } from "../../../utils/setLoadingMessage";
import { ensurePlansLoadedForResume, CAROUSEL_RESUME_STEPS } from "./proposalSteps";
import { loadFilteredCategories } from "./leadSteps";
import leadSteps from "./leadSteps";
import copySteps from "./copySteps";
import proposalSteps from "./proposalSteps";
import pagamentoSteps from "./pagamento/pagamentoSteps";
import researchSteps from "./researchSteps";
import pagamentoCartaoSteps from "./pagamento/pagamentoCartaoSteps";
import pagamentoPixSteps from "./pagamento/pagamentoPixSteps";
import pagamentoBoletoSteps from "./pagamento/pagamentoBoletoSteps";
import errorSteps from "./errorSteps";
import cepErrorSteps from "./cepErrorSteps";
import cupomSteps from "./cupomSteps";
import insuranceSteps, {
  buildInsuranceSelectResumeMessage,
} from "./insuranceSteps";
import {
  buildPixQrcodeImageUrl,
  getPixDataFromCardItem,
  getBoletoDataFromCardItem,
  getPaymentImageUrlFromCardItem,
  historicoHasUnifiedOpeningBlock,
  hasUnifiedOpeningBlock,
  OPENING_BLOCK_STEP,
} from "../../../utils/buildStepStructuredItems";
import { resolveStepName } from "./stepNames";
import {
  CARD_PAYMENT_APPROVED_PLACEHOLDER,
  CARD_PAYMENT_REFUSED_GIF_URL,
  CARD_PAYMENT_REFUSED_PLACEHOLDER,
} from "../../../utils/paymentImageUrls";
import {
  historicoHasUnifiedProposalBlock,
  PROPOSAL_CONFIRM_PRELUDE_STEPS,
} from "../../../utils/stepMessageBlocks";
import {
  clearConversacaoIdFromUrl,
  getConversacaoIdFromUrl,
  shouldAutoResumeFromUrl,
} from "../../../utils/conversacaoUrlParams";

const isIntermediariaCopyStep = (step) =>
  step?.startsWith("copy_intermediaria_") || step?.startsWith("copy-message-");

const LOCAL_STORAGE_LEAD_KEY = "mycon_lead_id";
const LOCAL_STORAGE_CONVERSACAO_KEY = "mycon_conversacao_id";

const META_STEPS = ["verifica_retomada", "retomar_conversa"];

const INTERACTIVE_STEPS = new Set(
  [
    ...copySteps,
    ...leadSteps,
    ...proposalSteps,
    ...pagamentoSteps,
    ...researchSteps,
    ...pagamentoCartaoSteps,
    ...pagamentoPixSteps,
    ...pagamentoBoletoSteps,
    ...errorSteps,
    ...cepErrorSteps,
    ...cupomSteps,
    ...insuranceSteps,
  ]
    .filter(
      (step) =>
        step.inputType ||
        step.options ||
        step.useCarousel ||
        step.useCarouselPlan ||
        step.buttons ||
        step.stepButtons
    )
    .map((step) => step.name)
    .filter(Boolean)
);

const RESUME_SKIP_ACTION_STEPS = new Set([
  "inicio_pesquisa",
  "pesquisa_finalizada",
  "pergunta_algo_mais",
  "agradecimento_pesquisa",
  "conclusao_fluxo",
  "exibe_dados_cadastrais",
  "confirma_detalhes_plano",
]);

const NEXT_STEP_MAP = {
  "nome_completo":          "cpf_cnpj",
  "cpf_cnpj":            "cep",
  "cep":                 "agradecimento_cep",
  "cep_nao_encontrado":                    "tentar_cep_novamente",
  "tentar_cep_novamente":                    "tentar_cep_novamente",
  "erro_cep":                        "erro_cep",
  "informa_logradouro":                   "informa_logradouro",
  "informa_bairro":             "informa_bairro",
  "agradecimento_cep":          "aguarda_registro_dados",
  "aguarda_registro_dados":      "nascimento_fundacao",
  "nascimento_fundacao":                "revisao_dados_pessoais",
  "revisao_dados_pessoais":            "confirmacao_dados_cadastrais",
  "confirmacao_dados_cadastrais":           "termos_regulamento",
  "mensagem_resolucao_285":             "termos_regulamento",
  "termos_regulamento":                   "termos_regulamento",
  "exibe_dados_cadastrais":               "exibe_dados_cadastrais",
  "confirma_detalhes_plano":            "confirma_detalhes_plano",
  "menu_alterar_dados":            "menu_alterar_dados",
  "alterar_nome_completo":             "revisao_dados_pessoais",
  "alterar_email":            "revisao_dados_pessoais",
  "alterar_cpf_cnpj":         "revisao_dados_pessoais",
  "alterar_celular":            "revisao_dados_pessoais",
  "alterar_nascimento":             "revisao_dados_pessoais",
  "alterar_cep":              "revisao_dados_pessoais",
  "titular_plano":             "nome_completo",
  "confirmacao_informacoes":           "titular_plano",
  "aguarda_proposta":                              "inicio_pagamento",
  "proposta_criada":                           "inicio_pagamento",
  "sem_metodos_pagamento":                      "sem_metodos_pagamento",
  "inicio_pagamento":                               "inicio_pagamento",
  "primeira_parcela":                       "inicio_pagamento",
  "proposta_gerada":                             "inicio_pagamento",
  "gerando_boleto":                   "inicio_pagamento",
  "parabens_boleto":                     "opcoes_boleto",
  "opcoes_boleto":                      "opcoes_boleto",
  "codigo_boleto":             "codigo_boleto",
  "exibe_codigo_boleto":                "codigo_boleto",
  "copiar_codigo_boleto":                "codigo_boleto",
  "abrir_app_banco":                       "abrir_app_banco",
  "proxima_assembleia":                            "proxima_assembleia",
  "erro_boleto_tentar_novamente":           "opcoes_boleto",
  "registro_pix":                      "inicio_pagamento",
  "conquista_iniciada":                    "gerando_pix",
  "gerando_pix":                         "gerando_pix",
  "mensagem_qrcode_pix":                  "gerando_pix",
  "qrcode_pix":                     "gerando_pix",
  "instrucao_scan_qrcode":                     "gerando_pix",
  "retorno_qrcode_pix":                 "gerando_pix",
  "escolha_copia_cola":                      "gerando_pix",
  "codigo_pix_copia_cola":              "gerando_pix",
  "exibe_codigo_pix":                 "gerando_pix",
  "botao_copiar_pix":               "gerando_pix",
  "confirma_pagamento_pix":              "gerando_pix",
  "instrucao_banco_pix":         "gerando_pix",
  "seleciona_banco_pix":  "gerando_pix",
  "retorno_copia_cola":             "gerando_pix",
  "confirmacao_pagamento_pix":            "gerando_pix",
  "erro_confirmacao_pix":     "gerando_pix",
  "confirma_pagamento_banco_pix":       "gerando_pix",
  "confirma_pagamento_banco_novamente": "gerando_pix",
  "email_pix_enviado":                        "gerando_pix",
  "mensagem_importante_pix":              "gerando_pix",
  "mensagem_para_voce_pix":                     "gerando_pix",
  "erro_pix_tentar_novamente":              "inicio_pagamento",
  "mensagem_seguranca_cartao":          "mensagem_seguranca_cartao",
  "escolha_parcelas_cartao":          "mensagem_seguranca_cartao",
  "valor_parcelas_cartao":    "mensagem_seguranca_cartao",
  "formulario_cartao":                  "mensagem_seguranca_cartao",
  "processando_cartao":                          "processando_cartao",
  "cartao_pendente":                 "cartao_pendente",
  "cartao_recusado":                             "cartao_recusado",
  "pagamento_pendente":                             "pagamento_pendente",
  "sucesso_cartao":                       "compra_finalizada",
  "pagamento_aprovado":                             "compra_finalizada",
  "alterar_forma_pagamento":                           "alterar_forma_pagamento",
  "compra_finalizada":                           "compra_finalizada",
  "continuar_pagamento":                            "compra_finalizada",
  "pergunta_mesmo_credito":                     "compra_finalizada",
  "inicio_pesquisa":                             "inicio_pesquisa",
  "pergunta_conhecer_mycon":                              "pergunta_conhecer_mycon",
  "pergunta_avaliacao_mycon":                               "pergunta_avaliacao_mycon",
  "pergunta_ajuda_especialista":                            "pergunta_ajuda_especialista",
  "pergunta_nota_especialista":                  "pergunta_nota_especialista",
  "pergunta_comentario_especialista":                "pergunta_comentario_especialista",
  "agradecimento_pesquisa":                      "agradecimento_pesquisa",
  "pesquisa_finalizada":                         "pesquisa_finalizada",
  "pergunta_algo_mais":                          "pergunta_algo_mais",
  "conclusao_fluxo":                           "conclusao_fluxo",
  "mensagem_carrossel_planos": "carrega_planos",
  "selecao_planos":                "carrega_planos",
  "carrega_planos": "carrega_planos",
  "pergunta_celular":                   "carrega_planos",
  "pergunta_email":                   "pergunta_celular",
  "mensagem_email":           "pergunta_email",
  "pergunta_valor_renda":               "mensagem_email",
  "planos_disponiveis":              "pergunta_valor_renda",
  "mensagem_lista_planos":           "planos_disponiveis",
  "informa_valor_credito":            "mensagem_lista_planos",
  "pergunta_valor_credito":                "pergunta_valor_credito",
  "introducao_simulacao":                "pergunta_valor_credito",
  "saudacao_nome":                   "introducao_simulacao",
  "pergunta_nome":                  "saudacao_nome",
  "escolha_categoria":             "pergunta_nome",
  "carrega_categorias":              "carrega_categorias",
  "registra_lead":                       "carrega_planos",
  "escolha_tipo_plano":               "escolha_tipo_plano",
  "tabela_servicos_completa":        "tabela_servicos_completa",
  "erro_renda_insuficiente":       "erro_renda_insuficiente",
  "sem_renda_extra":                "sem_renda_extra",
  "pergunta_renda_extra":                "pergunta_renda_extra",
  "renda_extra_confirmada":          "renda_extra_confirmada",
  "erro_sem_planos":                 "erro_sem_planos",
  "erro_proposta":                 "erro_proposta",
  "erro_geral":                  "erro_geral",
  "erro_conexao_inicio":         "erro_conexao",
  "erro_conexao":       "erro_conexao",
  "mensagem_desconto_parcela":      "valor_desconto_parcela",
  "valor_desconto_parcela":         "confirmacao_informacoes",
  "comprar_outra_cota":             "comprar_outra_cota",
  "valida_seguro":             "apresentacao_seguro",
  "apresentacao_seguro":      "apresentacao_seguro",
  "descricao_seguro":  "escolha_seguro",
  "escolha_seguro":               "escolha_seguro",
  "termos_seguro":                 "termos_seguro",
  "pergunta_cupom":          "pergunta_cupom",
  "informa_codigo_cupom":                    "informa_codigo_cupom",
  "erro_cupom":          "erro_cupom_opcoes",
  "erro_cupom_opcoes":          "erro_cupom_opcoes",
  "tentar_cupom_novamente":                "tentar_cupom_novamente",
  "cupom_aplicado":            "novo_valor_com_cupom",
  "novo_valor_com_cupom":          "mensagem_pagamento_cupom",
  "mensagem_pagamento_cupom":          "mensagem_pagamento_cupom",
  "cupom_url_detectado":              "aplica_cupom_url",
  "aplica_cupom_url":                "aplica_cupom_url",
  "copy_intermediaria_2":                   "carrega_categorias",
  "copy_inicial":                   "copy_inicial",
};

const clearResumeStorage = () => {
  localStorage.removeItem(LOCAL_STORAGE_LEAD_KEY);
  localStorage.removeItem(LOCAL_STORAGE_CONVERSACAO_KEY);
  localStorage.removeItem("mycon_selected_plano");
  localStorage.removeItem("mycon_fetched_proposta");
  localStorage.removeItem("mycon_fetched_plan_details");
  localStorage.removeItem("mycon_payment_boleto");
  localStorage.removeItem("mycon_payment_pix");
  clearResumeExtraStorage();
};

const startFreshSimulation = async () => {
  clearResumeStorage();
  clearConversacaoIdFromUrl();
  useConversacaoStore.getState().setConversacao({
    id: "",
    stage: "",
    metadata: {},
    historico: [],
  });
  goToCopyInicial();
};

const goToCopyInicial = () => {
  useBotControlStore.getState().clearPendingMessageBlock();
  useBotControlStore.setState({ currentStep: findStep("copy_inicial").index });
};

const getCleanHistorico = (historico) => {
  const firstMetaIndex = historico.findIndex((e) => META_STEPS.includes(e.step));
  return firstMetaIndex === -1 ? historico : historico.slice(0, firstMetaIndex);
};

const sortHistoricoEntries = (entries) =>
  [...entries].sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    if (ta !== tb) return ta - tb;
    if (a.step !== b.step) return 0;
    if (a.sender === b.sender) return 0;
    return a.sender === "bot" ? -1 : 1;
  });

const PAYMENT_STEPS = new Set([
  "inicio_pagamento", "primeira_parcela", "proposta_gerada",
  "alterar_forma_pagamento",
  "gerando_boleto", "parabens_boleto",
  "opcoes_boleto",
  "codigo_boleto", "exibe_codigo_boleto", "copiar_codigo_boleto",
  "abrir_app_banco", "proxima_assembleia",
  "erro_boleto_tentar_novamente",
  "registro_pix", "conquista_iniciada",
  "gerando_pix",
  "mensagem_qrcode_pix", "qrcode_pix", "instrucao_scan_qrcode",
  "retorno_qrcode_pix",
  "escolha_copia_cola",
  "codigo_pix_copia_cola", "exibe_codigo_pix",
  "botao_copiar_pix", "confirma_pagamento_pix",
  "instrucao_banco_pix", "seleciona_banco_pix",
  "retorno_copia_cola",
  "confirmacao_pagamento_pix", "erro_confirmacao_pix",
  "confirma_pagamento_banco_pix", "confirma_pagamento_banco_novamente",
  "email_pix_enviado", "mensagem_importante_pix", "mensagem_para_voce_pix",
  "erro_pix_tentar_novamente",
  "mensagem_seguranca_cartao", "escolha_parcelas_cartao",
  "valor_parcelas_cartao", "formulario_cartao",
  "processando_cartao", "cartao_pendente",
  "cartao_recusado", "pagamento_pendente",
  "sucesso_cartao", "pagamento_aprovado",
  "compra_finalizada", "continuar_pagamento", "pergunta_mesmo_credito",
  "inicio_pesquisa", "pergunta_conhecer_mycon", "pergunta_avaliacao_mycon",
  "pergunta_ajuda_especialista", "pergunta_nota_especialista", "pergunta_comentario_especialista",
  "agradecimento_pesquisa", "pesquisa_finalizada", "pergunta_algo_mais", "conclusao_fluxo",
  "sem_metodos_pagamento",
]);

const STEPS_TO_SKIP_IN_HISTORY = new Set([
  "termos_regulamento",
  "confirma_detalhes_plano",
  "revisao_dados_pessoais",
]);

const RESUME_STEPS_USE_HISTORICO_TEXT = new Set(["informa_valor_credito"]);

const isWaitingForUserOnResumeStep = (filtered, resumeStep) =>
  !!(
    resumeStep &&
    filtered.length &&
    filtered[filtered.length - 1].step === resumeStep &&
    filtered[filtered.length - 1].sender === "bot"
  );

const parseCreditRangesFromMessage = (message = "") => {
  const match = message.match(/R\$\s*([\d.]+,\d{2})\s*a\s*R\$\s*([\d.]+,\d{2})/i);
  if (!match) return null;

  const parseValue = (value) =>
    parseFloat(value.replace(/\./g, "").replace(",", "."));

  const minBemValor = parseValue(match[1]);
  const maxBemValor = parseValue(match[2]);

  if (!minBemValor || !maxBemValor) return null;

  return { minBemValor, maxBemValor };
};

const restoreRangesFromHistorico = (historico) => {
  const { ranges } = useConsorcioStore.getState();
  if (ranges?.minBemValor > 0 && ranges?.maxBemValor > 0) return;

  const creditRangeEntry = [...historico]
    .reverse()
    .find(
      (entry) =>
        entry.step === "informa_valor_credito" &&
        entry.sender === "bot" &&
        entry.items?.[0]?.type === "text"
    );

  if (!creditRangeEntry) return;

  const parsed = parseCreditRangesFromMessage(creditRangeEntry.items[0].message);
  if (!parsed) return;

  useConsorcioStore.setState((state) => ({ ...state, ranges: parsed }));
};

const getLastBotEntryIndex = (sorted, stepName) => {
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    if (sorted[i].step === stepName && sorted[i].sender === "bot") {
      return i;
    }
  }
  return -1;
};

const getUserAnswerAfterLastBot = (sorted, stepName) => {
  const lastBotIndex = getLastBotEntryIndex(sorted, stepName);
  if (lastBotIndex === -1) return null;

  return (
    sorted
      .slice(lastBotIndex + 1)
      .find((entry) => entry.step === stepName && entry.sender === "user") ??
    null
  );
};

const getResumeStepAfterOrphanPaymentTail = (sorted) => {
  const compraBotIndex = getLastBotEntryIndex(sorted, "compra_finalizada");
  if (compraBotIndex === -1) return null;

  if (getUserAnswerAfterLastBot(sorted, "compra_finalizada")) return null;

  const afterCompra = sorted.slice(compraBotIndex + 1);

  if (
    afterCompra.length === 0 ||
    afterCompra.every((entry) => POST_PIX_ORPHAN_STEPS.has(entry.step))
  ) {
    return "compra_finalizada";
  }

  return null;
};

const getLastVerOutrosPlanosIndex = (sorted) => {
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const entry = sorted[i];
    if (
      entry.step === "confirmacao_informacoes" &&
      entry.sender === "user" &&
      entry.items?.[0]?.message === "VER OUTROS PLANOS"
    ) {
      return i;
    }
  }
  return -1;
};

const QRCODE_PIX_PLACEHOLDER = "[QRCODE PIX]";

const isPixQrcodeCardItem = (item) =>
  item?.type === "card_pix" && item?.message?.tipo === "qrcode";

const hasPixQrcodeInStore = () => {
  const pix = useUserStore.getState().payment?.pix ?? {};
  return Boolean(buildPixQrcodeImageUrl(pix.qrCode, pix.url));
};

const restorePixDataToStore = (pixCardItem) => {
  const pixData = pixCardItem ? getPixDataFromCardItem(pixCardItem) : null;
  if (!pixData) return;

  useUserStore.setState((state) => ({
    payment: {
      ...state.payment,
      pix: { ...state.payment?.pix, ...pixData },
    },
  }));
};

const buildPixQrcodeResumeMessage = (time, pixCardItem) => {
  restorePixDataToStore(pixCardItem);

  return {
    sender: "bot",
    text: "",
    time,
    component: React.createElement(PixPaymentImage),
    loading: null,
    buttons: [],
    name: "qrcode_pix",
    shouldPersistComponent: true,
  };
};

const buildMessagesFromHistorico = (historico, resumeStep) => {
  const time = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const messages = [];

  const filtered = sortHistoricoEntries(
    historico
      .filter((entry) => !META_STEPS.includes(entry.step))
      .filter(
        (entry) => !(STEPS_TO_SKIP_IN_HISTORY.has(entry.step) && entry.sender === "bot")
      )
  );

  let truncateFrom = -1;
  const waitingForUserInput = isWaitingForUserOnResumeStep(filtered, resumeStep);

  if (resumeStep && CAROUSEL_RESUME_STEPS.has(resumeStep)) {
    const verOutrosIndex = getLastVerOutrosPlanosIndex(filtered);
    if (verOutrosIndex !== -1) {
      truncateFrom = verOutrosIndex + 1;
    }
  }

  if (resumeStep && !waitingForUserInput && !RESUME_STEPS_USE_HISTORICO_TEXT.has(resumeStep)) {
    if (PAYMENT_STEPS.has(resumeStep)) {
      if (getResumeStepAfterOrphanPaymentTail(filtered) === resumeStep) {
        const compraBotIndex = getLastBotEntryIndex(filtered, resumeStep);
        if (compraBotIndex !== -1) {
          truncateFrom = compraBotIndex + 1;
        }
      } else {
        const resumeStepIndex = filtered.reduce(
          (last, e, i) => (e.step === resumeStep ? i : last),
          -1
        );
        if (resumeStepIndex !== -1) {
          truncateFrom = resumeStepIndex + 1;
        }
      }
    } else if (
      filtered.length &&
      filtered[filtered.length - 1].step === resumeStep
    ) {
      let i = filtered.length - 1;
      while (i >= 0 && filtered[i].step === resumeStep) i--;
      truncateFrom = i + 1;
    }
  }

  const lastIdxByStepSender = new Map();
  filtered.forEach((e, i) => {
    if (PAYMENT_STEPS.has(e.step)) lastIdxByStepSender.set(`${e.step}|${e.sender}`, i);
  });

  const unifiedOpeningBlock = historicoHasUnifiedOpeningBlock(filtered);
  const unifiedProposalBlock = historicoHasUnifiedProposalBlock(filtered);

  filtered
    .forEach((entry, idx) => {
      if (truncateFrom !== -1 && idx >= truncateFrom) return;

      if (resumeStep && entry.step === resumeStep && entry.sender === "user") return;

      if (
        unifiedOpeningBlock &&
        entry.sender === "bot" &&
        isIntermediariaCopyStep(entry.step) &&
        resolveStepName(entry.step) !== OPENING_BLOCK_STEP
      ) {
        return;
      }

      if (
        unifiedProposalBlock &&
        entry.sender === "bot" &&
        PROPOSAL_CONFIRM_PRELUDE_STEPS.has(entry.step)
      ) {
        return;
      }

      if (
        PAYMENT_STEPS.has(entry.step) &&
        lastIdxByStepSender.get(`${entry.step}|${entry.sender}`) !== idx
      )
        return;

      const textItems = (entry.items ?? []).filter((item) => item.type === "text" && item.message);
      const pixCardItem = (entry.items ?? []).find((item) => item.type === "card_pix");
      const imageCardItem = (entry.items ?? []).find((item) => item.type === "card_image");
      if (!textItems.length && !pixCardItem && !imageCardItem) return;

      if (entry.step === "confirmacao_dados_cadastrais" && entry.sender === "bot" && textItems.length > 1) {
        const combined = textItems
          .map((item) => item.message.replace(/^([^:]+):/, "<b> $1:</b>"))
          .join("<br />");
        messages.push({
          sender: "bot",
          text: `<div class="message-ellipsis">${combined}</div>`,
          time,
          component: null,
          loading: null,
          buttons: [],
          name: null,
          shouldPersistComponent: false,
        });
        return;
      }

      if (
        entry.step === OPENING_BLOCK_STEP &&
        entry.sender === "bot" &&
        hasUnifiedOpeningBlock(entry.items ?? [])
      ) {
        (entry.items ?? []).forEach((item) => {
          if (item.type === "text" && item.message) {
            messages.push({
              sender: "bot",
              text: item.message,
              time,
              component: null,
              loading: null,
              buttons: [],
              name: null,
              shouldPersistComponent: false,
            });
          }
        });
        return;
      }

      if (
        entry.step === "qrcode_pix" &&
        entry.sender === "bot" &&
        (textItems.some((item) => item.message === QRCODE_PIX_PLACEHOLDER) ||
          pixCardItem ||
          hasPixQrcodeInStore())
      ) {
        messages.push(buildPixQrcodeResumeMessage(time, pixCardItem));
        return;
      }

      if (
        isPixQrcodeCardItem(pixCardItem) &&
        !messages.some((message) => message.name === "qrcode_pix")
      ) {
        messages.push(buildPixQrcodeResumeMessage(time, pixCardItem));
        return;
      }

      if (entry.step === "cartao_recusado" && entry.sender === "bot") {
        const errorText = textItems.find(
          (item) => item.message !== CARD_PAYMENT_REFUSED_PLACEHOLDER
        )?.message;
        const imageUrl =
          getPaymentImageUrlFromCardItem(imageCardItem) ??
          (textItems.some((item) => item.message === CARD_PAYMENT_REFUSED_PLACEHOLDER)
            ? CARD_PAYMENT_REFUSED_GIF_URL
            : null);

        if (errorText || imageUrl) {
          messages.push({
            sender: "bot",
            text: errorText ?? "",
            time,
            component: imageUrl
              ? React.createElement("img", {
                  className: "card-payment",
                  src: imageUrl,
                  alt: "Cartão recusado",
                })
              : null,
            loading: null,
            buttons: [],
            name: "cartao_recusado",
            shouldPersistComponent: Boolean(imageUrl),
          });
          return;
        }
      }

      if (entry.step === "pagamento_aprovado" && entry.sender === "bot") {
        const successText = textItems.find(
          (item) => item.message !== CARD_PAYMENT_APPROVED_PLACEHOLDER
        )?.message;
        const imageUrl = getPaymentImageUrlFromCardItem(imageCardItem);

        if (successText || imageUrl) {
          messages.push({
            sender: "bot",
            text: successText ?? "",
            time,
            component: imageUrl
              ? React.createElement("img", {
                  className: "payment-image",
                  src: imageUrl,
                  alt: "Pagamento aprovado",
                })
              : null,
            loading: null,
            buttons: [],
            name: "pagamento_aprovado",
            shouldPersistComponent: Boolean(imageUrl),
          });
          return;
        }
      }

      if (
        entry.step === "escolha_seguro" &&
        entry.sender === "bot" &&
        resumeStep === "escolha_seguro"
      ) {
        const text =
          textItems.map((item) => item.message).join(" ") ||
          "Selecione o seguro prestamista:";
        messages.push(buildInsuranceSelectResumeMessage(time, text));
        return;
      }

      textItems.forEach((item) => {
        messages.push({
          sender: entry.sender,
          text: item.message,
          time,
          component: null,
          loading: null,
          buttons: [],
          name: null,
          shouldPersistComponent: false,
        });
      });
    });

  return messages;
};

const restoreStoreFromHistorico = (historico, leadId) => {
  if (leadId) {
    useUserStore.setState({ lead: { LeadID: leadId, LeadToken: "" } });
  }

  restoreResumeStorage();
  restoreRangesFromHistorico(historico);

  try {
    const savedProposta = localStorage.getItem("mycon_fetched_proposta");
    if (savedProposta) {
      useConsorcioStore.setState({ fetchedProposta: JSON.parse(savedProposta) });
    }
  } catch (_) {}

  try {
    const savedPlanDetails = localStorage.getItem("mycon_fetched_plan_details");
    if (savedPlanDetails) {
      useConsorcioStore.setState({ fetchedPlanDetails: JSON.parse(savedPlanDetails) });
    }
  } catch (_) {}

  try {
    const savedBoleto = localStorage.getItem("mycon_payment_boleto");
    if (savedBoleto) {
      useUserStore.setState((state) => ({
        payment: { ...state.payment, boleto: JSON.parse(savedBoleto) },
      }));
    }
  } catch (_) {}

  try {
    const savedPix = localStorage.getItem("mycon_payment_pix");
    if (savedPix) {
      const parsedPix = JSON.parse(savedPix);
      useUserStore.setState((state) => ({
        payment: {
          ...state.payment,
          pix: {
            ...parsedPix,
            url:
              buildPixQrcodeImageUrl(parsedPix.qrCode, parsedPix.url) ??
              parsedPix.url,
          },
        },
      }));
    }
  } catch (_) {}

  const latestPixCardEntry = [...historico]
    .reverse()
    .find(
      (entry) =>
        entry.sender === "bot" &&
        entry.items?.some((item) => item.type === "card_pix")
    );

  if (latestPixCardEntry) {
    const pixCardItem = latestPixCardEntry.items.find(
      (item) => item.type === "card_pix"
    );
    const pixData = getPixDataFromCardItem(pixCardItem);

    if (pixData) {
      useUserStore.setState((state) => ({
        payment: {
          ...state.payment,
          pix: { ...state.payment?.pix, ...pixData },
        },
      }));
    }
  }

  const latestBoletoCardEntry = [...historico]
    .reverse()
    .find(
      (entry) =>
        entry.sender === "bot" &&
        entry.items?.some((item) => item.type === "card_boleto")
    );

  if (latestBoletoCardEntry) {
    const boletoCardItem = latestBoletoCardEntry.items.find(
      (item) => item.type === "card_boleto"
    );
    const boletoData = getBoletoDataFromCardItem(boletoCardItem);

    if (boletoData) {
      useUserStore.setState((state) => ({
        payment: {
          ...state.payment,
          boleto: { ...state.payment?.boleto, ...boletoData },
        },
      }));
    }
  }

  historico
    .filter((entry) => entry.sender === "user" && !META_STEPS.includes(entry.step))
    .forEach((entry) => {
      const userMessage = entry.items?.[0]?.message ?? "";

      switch (entry.step) {
        case "pergunta_nome":
          useUserStore.getState().setUserBasicInfo(userMessage, "nome");
          break;
        case "informa_valor_credito":
          useUserStore.getState().setUserBasicInfo(
            parseFloat(userMessage.replace(/[^\d,]/g, "").replace(",", ".")) || userMessage,
            "valorCredito"
          );
          break;
        case "pergunta_valor_renda":
          useUserStore.getState().setUserBasicInfo(
            parseFloat(userMessage.replace(/[^\d,]/g, "").replace(",", ".")) || userMessage,
            "rendaMensal"
          );
          break;
        case "pergunta_email":
          useUserStore.getState().setUserBasicInfo(userMessage, "email");
          break;
        case "pergunta_celular":
          useUserStore.getState().setUserBasicInfo(userMessage, "telefone");
          break;
        case "escolha_categoria": {
          const categoriaId = parseInt(
            Object.keys(CategoriasEnum).find(
              (id) => CategoriasEnum[id].title === userMessage
            ) || "0"
          );
          if (categoriaId) {
            useConsorcioStore.setState((state) => ({
              ...state,
              categoriaId,
              tipoBem: categoryNames[categoriaId] ?? userMessage,
              tipoPlano: CategoriasEnum[categoriaId]?.tipoPlano ?? "",
            }));
          }
          break;
        }
        case "titular_plano":
          useUserStore.setState({
            ...useUserStore.getState(),
            contrato: {
              ...useUserStore.getState().contrato,
              tipoPessoa: userMessage === "EU MESMO" ? 0 : 1,
              tipoCliente: userMessage === "EU MESMO" ? "Pessoa Física" : "Pessoa Jurídica",
            },
          });
          break;
        case "nome_completo":
          useUserStore.getState().setUserContratoInfo(userMessage, "nomeRazao");
          break;
        case "cpf_cnpj":
          useUserStore.getState().setUserContratoInfo(userMessage, "cpfCnpj");
          break;
        case "cep":
          useUserStore.getState().setUserContratoInfo(
            userMessage.replace(/\D/g, ""),
            "cep"
          );
          break;
        case "nascimento_fundacao":
          useUserStore.getState().setUserContratoInfo(userMessage, "nascimentoFundacao");
          break;
        case "tentar_cep_novamente":
        case "alterar_cep":
          useUserStore.getState().setUserContratoInfo(
            userMessage.replace(/\D/g, ""),
            "cep"
          );
          break;
        case "informa_logradouro":
          useUserStore.getState().setUserContratoInfo(userMessage, "logradouro");
          break;
        case "informa_bairro":
          useUserStore.getState().setUserContratoInfo(userMessage, "bairro");
          break;
        case "pergunta_renda_extra":
          useUserStore.getState().setUserBasicInfo(
            parseFloat(userMessage.replace(/[^\d,]/g, "").replace(",", ".")) || userMessage,
            "rendaExtra"
          );
          break;
        case "sem_renda_extra":
          useUserStore.getState().setUserBasicInfo(
            parseFloat(userMessage.replace(/[^\d,]/g, "").replace(",", ".")) || userMessage,
            "valorCredito"
          );
          break;
        case "informa_codigo_cupom":
        case "tentar_cupom_novamente":
          useUserStore.setState({
            ...useUserStore.getState(),
            userCupom: userMessage,
          });
          break;
        case "alterar_nome_completo":
          useUserStore.getState().setUserContratoInfo(userMessage, "nomeRazao");
          break;
        case "alterar_email":
          useUserStore.getState().setUserContratoInfo(userMessage, "email");
          break;
        case "alterar_cpf_cnpj":
          useUserStore.getState().setUserContratoInfo(userMessage, "cpfCnpj");
          break;
        case "alterar_celular":
          useUserStore.getState().setUserContratoInfo(userMessage, "telefone");
          break;
        case "alterar_nascimento":
          useUserStore.getState().setUserContratoInfo(userMessage, "nascimentoFundacao");
          break;
        case "pergunta_conhecer_mycon":
          useUserStore.setState({
            ...useUserStore.getState(),
            pesquisa: { ...useUserStore.getState().pesquisa, midia: userMessage },
          });
          break;
        case "pergunta_avaliacao_mycon":
          useUserStore.setState({
            ...useUserStore.getState(),
            pesquisa: { ...useUserStore.getState().pesquisa, csatcompra: userMessage },
          });
          break;
        case "pergunta_nota_especialista":
          useUserStore.setState({
            ...useUserStore.getState(),
            pesquisa: { ...useUserStore.getState().pesquisa, csathumanos: userMessage },
          });
          break;
        case "pergunta_comentario_especialista":
          useUserStore.setState({
            ...useUserStore.getState(),
            pesquisa: { ...useUserStore.getState().pesquisa, comentarios: userMessage },
          });
          break;
        case "mensagem_carrossel_planos": {
          try {
            const saved = localStorage.getItem("mycon_selected_plano");
            if (saved) {
              const parsedPlano = JSON.parse(saved);
              useConsorcioStore.setState({ selectedPlano: parsedPlano });
              usePaymentStore.setState({
                firstParcel: parsedPlano.planoValorPagamento || 0,
                monthlyParcel: parsedPlano.planoValorParcela || 0,
                insuranceFirstParcel: parsedPlano.valorParcelaPrimeiroPagtoComSeguro || 0,
                insuranceMonthlyParcel: parsedPlano.valorParcelaComSeguro || 0,
              });
              break;
            }
          } catch (_) {}
          const parseValue = (str) =>
            parseFloat(str.replace(/[R$\s.]/g, "").replace(",", ".")) || 0;
          const mesesMatch = userMessage.match(/em\s+(\d+)\s+parcelas/);
          const parcelaMatch = userMessage.match(/parcelas de R\$[\s\u00a0]*([\d.,]+)/);
          const creditoMatch = userMessage.match(/^R\$[\s\u00a0]*([\d.,]+)/);
          const parcelaValue = parcelaMatch ? parseValue(parcelaMatch[1]) : 0;
          useConsorcioStore.setState((state) => ({
            selectedPlano: {
              ...state.selectedPlano,
              ...(creditoMatch && { bemCredito: parseValue(creditoMatch[1]) }),
              ...(mesesMatch && { planoMeses: parseInt(mesesMatch[1]) }),
              ...(parcelaMatch && { planoValorParcela: parcelaValue }),
            },
          }));
          if (parcelaValue) {
            usePaymentStore.setState({ firstParcel: parcelaValue, monthlyParcel: parcelaValue });
          }
          break;
        }
        default:
          break;
      }
    });

  const insuranceChoice = [...historico]
    .reverse()
    .find(
      (e) =>
        e.step === "escolha_seguro" &&
        e.sender === "user" &&
        !META_STEPS.includes(e.step)
    );
  if (insuranceChoice) {
    const variablesItem = insuranceChoice.items?.find(
      (item) => item.type === "variables"
    );
    const textItem = insuranceChoice.items?.find((item) => item.type === "text");
    const msg = textItem?.message ?? "";

    let isInsuranceHired;
    if (variablesItem?.message?.seguro !== undefined) {
      isInsuranceHired = Boolean(variablesItem.message.seguro);
    } else if (msg === "Ótima escolha!") {
      isInsuranceHired = true;
    } else if (msg.includes("seguir sem o seguro")) {
      isInsuranceHired = false;
    }

    if (isInsuranceHired !== undefined) {
      useInsuranceStore.setState({ isInsuranceHired });
    }
  }
};

const STEPS_BEFORE_PROPOSAL_CREATION = new Set([
  "nome_completo",
  "cpf_cnpj",
  "cep",
  "agradecimento_cep",
  "aguarda_registro_dados",
]);

const SHARED_PAYMENT_TAIL = new Set(["abrir_app_banco", "proxima_assembleia"]);

const POST_PIX_ORPHAN_STEPS = new Set([
  "botao_copiar_pix",
  "confirma_pagamento_pix",
  "abrir_app_banco",
  "proxima_assembleia",
]);

const PIX_DEFERRED_INTERACTIVE_STEPS = new Set([
  "exibe_codigo_pix",
  "codigo_pix_copia_cola",
  "mensagem_qrcode_pix",
  "qrcode_pix",
]);

const CARD_PAYMENT_OUTCOME_STEPS = new Set([
  "cartao_recusado",
  "pagamento_pendente",
  "pagamento_aprovado",
  "cartao_pendente",
]);

const CHANGE_INFO_TARGETS = {
  "NOME": "alterar_nome_completo",
  "E-MAIL": "alterar_email",
  "CPF/CNPJ": "alterar_cpf_cnpj",
  "CELULAR": "alterar_celular",
  "NASCIMENTO": "alterar_nascimento",
  "CEP": "alterar_cep",
  "NENHUMA": "revisao_dados_pessoais",
};

const PAYMENT_METHOD_TARGETS = {
  "PIX": "registro_pix",
  "CARTÃO (3X SEM JUROS)": "mensagem_seguranca_cartao",
  "BOLETO": "gerando_boleto",
  "PAGAR COM PIX": "registro_pix",
  "PAGAR COM BOLETO": "gerando_boleto",
  "TENTAR NOVAMENTE": "mensagem_seguranca_cartao",
};

const PIX_OPTION_TARGETS = {
  "QRCODE": "mensagem_qrcode_pix",
  "COPIA E COLA": "codigo_pix_copia_cola",
};

const BOLETO_OPTION_TARGETS = {
  "COPIAR CÓDIGO": "codigo_boleto",
  "ABRIR BOLETO": "abrir_app_banco",
};

const PIX_CONFIRMATION_TARGETS = {
  "SIM": "erro_confirmacao_pix",
  "NÃO": "confirmacao_pagamento_pix",
};

const CEP_FLOW_STEPS = new Set([
  "cep_nao_encontrado",
  "tentar_cep_novamente",
  "informa_logradouro",
  "informa_bairro",
  "erro_cep",
]);

const POST_PROPOSTA_STEPS = new Set([
  "aguarda_proposta",
  "proposta_criada",
]);

const getPostConfirmStep = () => {
  const { seguro, shouldUseCoupon } = useInitialConfigStore.getState();

  if (seguro) {
    return "apresentacao_seguro";
  }

  if (shouldUseCoupon) {
    return hasUrlCoupon() ? "cupom_url_detectado" : "pergunta_cupom";
  }

  return "aguarda_proposta";
};

const inferCepErrorAt = (historico) => {
  const clean = historico.filter((e) => !META_STEPS.includes(e.step));
  const isUpdateFlow = clean.some(
    (e) =>
      e.step === "alterar_cep" ||
      (e.step === "menu_alterar_dados" &&
        e.sender === "user" &&
        e.items?.[0]?.message === "CEP")
  );
  return isUpdateFlow ? "update" : "cep-first-input";
};

const CEP_FLOW_CONTEXT_STEPS = new Set([
  ...CEP_FLOW_STEPS,
  "cep",
  "alterar_cep",
]);

const getCepFlowResumeStep = (historico, lastEntry) => {
  const lastStep = resolveStepName(lastEntry.step);

  if (lastStep === "erro_geral" || lastStep === "erro_proposta") {
    return null;
  }

  const sorted = sortHistoricoEntries(
    getCleanHistorico(historico).filter((e) => !META_STEPS.includes(e.step))
  );

  const lastCepIdx = sorted.reduce(
    (last, e, i) => (CEP_FLOW_STEPS.has(e.step) ? i : last),
    -1
  );
  const lastCep = lastCepIdx === -1 ? null : sorted[lastCepIdx];

  const hasCepFailureAfterAdd = () => {
    if (lastEntry.step !== "cep" || lastEntry.sender !== "user") {
      return false;
    }
    const idx = sorted.lastIndexOf(lastEntry);
    return sorted
      .slice(idx + 1)
      .some((e) => e.step === "cep_nao_encontrado" || e.step === "tentar_cep_novamente");
  };

  const cepFlowStillActive =
    lastCepIdx === -1
      ? hasCepFailureAfterAdd() || lastEntry.step === "alterar_cep"
      : !sorted
          .slice(lastCepIdx + 1)
          .some((e) => !CEP_FLOW_CONTEXT_STEPS.has(e.step));

  const inCepFlow =
    cepFlowStillActive &&
    (!!lastCep || hasCepFailureAfterAdd() || lastEntry.step === "alterar_cep");

  if (!inCepFlow) return null;

  useUserStore.setState({
    cepErrorAt: inferCepErrorAt(historico),
  });

  if (lastCep?.step === "cep_nao_encontrado") return "tentar_cep_novamente";
  if (lastCep?.step === "tentar_cep_novamente") return "tentar_cep_novamente";

  const streetEntry = sorted.find(
    (e) => e.step === "informa_logradouro" && e.sender === "user"
  );
  const neighborhoodEntry = sorted.find(
    (e) => e.step === "informa_bairro" && e.sender === "user"
  );
  const { logradouro, bairro } = useUserStore.getState().contrato;
  const hasStreet = logradouro || streetEntry;
  const hasNeighborhood = bairro || neighborhoodEntry;

  if (lastCep?.step === "informa_logradouro" && !hasNeighborhood) {
    return streetEntry ? "informa_bairro" : "informa_logradouro";
  }
  if (!hasStreet) return "informa_logradouro";
  if (!hasNeighborhood) return "informa_bairro";

  return inferCepErrorAt(historico) === "update"
    ? "revisao_dados_pessoais"
    : "agradecimento_cep";
};

const PIX_QRCODE_RENDER_STEPS = new Set([
  "mensagem_qrcode_pix",
  "qrcode_pix",
  "instrucao_scan_qrcode",
]);

const PIX_COPY_PASTE_RENDER_STEPS = new Set([
  "codigo_pix_copia_cola",
  "exibe_codigo_pix",
]);

const getPaymentContext = (historico) => {
  let method = null;
  let pixOption = null;

  historico
    .filter((e) => !META_STEPS.includes(e.step))
    .forEach((e) => {
      if (e.step === "registro_pix" || e.step === "conquista_iniciada") {
        method = "pix";
      } else if (e.step === "gerando_boleto" || e.step === "parabens_boleto") {
        method = "boleto";
      }

      if (
        e.step === "gerando_pix" &&
        e.sender === "user" &&
        e.items?.[0]?.message === "QRCODE"
      ) {
        pixOption = "qrcode";
      } else if (
        e.step === "gerando_pix" &&
        e.sender === "user" &&
        e.items?.[0]?.message === "COPIA E COLA"
      ) {
        pixOption = "copy-paste";
      } else if (
        e.step === "mensagem_qrcode_pix" ||
        e.step === "qrcode_pix" ||
        e.step === "instrucao_scan_qrcode"
      ) {
        pixOption = "qrcode";
      } else if (
        e.step === "codigo_pix_copia_cola" ||
        e.step === "exibe_codigo_pix" ||
        e.step === "botao_copiar_pix"
      ) {
        pixOption = "copy-paste";
      }
    });

  return { method, pixOption };
};

const getUserAnswerEntry = (historico, lastEntry) => {
  if (lastEntry.sender === "user") {
    return lastEntry;
  }

  const sorted = sortHistoricoEntries(
    getCleanHistorico(historico).filter((e) => !META_STEPS.includes(e.step))
  );
  if (!sorted.length || sorted[sorted.length - 1].step !== lastEntry.step) {
    return null;
  }

  return getUserAnswerAfterLastBot(sorted, lastEntry.step);
};

const getPendingInteractiveBotStep = (sorted, excludeSteps = new Set()) => {
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const entry = sorted[i];
    if (entry.sender !== "bot") continue;
    if (!INTERACTIVE_STEPS.has(entry.step)) continue;
    if (excludeSteps.has(entry.step)) continue;

    const answered = sorted
      .slice(i + 1)
      .some((e) => e.step === entry.step && e.sender === "user");

    if (!answered) return entry.step;
  }

  return null;
};

const ensureCategoriesLoadedForResume = async (resumeStepName, historico = []) => {
  if (resumeStepName !== "escolha_categoria") return false;

  const sorted = sortHistoricoEntries(
    getCleanHistorico(historico).filter((e) => !META_STEPS.includes(e.step))
  );

  if (getUserAnswerAfterLastBot(sorted, "escolha_categoria")) return false;

  const { filteredCategories } = useConsorcioStore.getState();
  if (filteredCategories?.length > 0) return false;

  const loadedOptions = await loadFilteredCategories({ navigate: false });

  if (loadedOptions.length === 1) return true;

  if (loadedOptions.length > 1) {
    useBotControlStore.setState({
      skipStepTracking: true,
      skipStepAction: true,
      currentStep: findStep("escolha_categoria").index,
    });
    return true;
  }

  return false;
};

const getResumeStep = (historico) => {
  const sorted = sortHistoricoEntries(
    getCleanHistorico(historico).filter((e) => !META_STEPS.includes(e.step))
  );
  const lastEntry = sorted[sorted.length - 1];

  if (!lastEntry) return "copy_inicial";

  let nextStep = NEXT_STEP_MAP[resolveStepName(lastEntry.step)] ?? "copy_inicial";

  const userAnswerEntry = getUserAnswerEntry(historico, lastEntry);
  const lastUserMessage =
    userAnswerEntry?.items?.[0]?.message ?? lastEntry.items?.[0]?.message;

  if (userAnswerEntry) {
    const userStep = resolveStepName(userAnswerEntry.step);
    if (lastUserMessage === "ALTERAR FORMA DE PAGAMENTO") {
      nextStep = "alterar_forma_pagamento";
    } else if (lastUserMessage === "DETALHES DO PLANO") {
      nextStep = "confirma_detalhes_plano";
    } else if (lastUserMessage === "ALTERAR DADOS") {
      nextStep = "menu_alterar_dados";
    } else if (
      lastUserMessage === "CONFIRMAR" &&
      (userStep === "exibe_dados_cadastrais" || userStep === "confirma_detalhes_plano")
    ) {
      nextStep = getPostConfirmStep();
    } else if (
      lastUserMessage === "CONFIRMAR" &&
      userStep === "mensagem_pagamento_cupom"
    ) {
      nextStep = "aguarda_proposta";
    } else if (
      userStep === "confirmacao_informacoes" &&
      lastUserMessage === "VER OUTROS PLANOS"
    ) {
      nextStep = "carrega_planos";
    } else if (
      userStep === "confirmacao_informacoes" &&
      lastUserMessage === "SEGUIR EM FRENTE"
    ) {
      nextStep = "titular_plano";
    } else if (
      userStep === "menu_alterar_dados" &&
      CHANGE_INFO_TARGETS[lastUserMessage]
    ) {
      nextStep = CHANGE_INFO_TARGETS[lastUserMessage];
    } else if (
      (userStep === "proposta_gerada" ||
        userStep === "alterar_forma_pagamento" ||
        userStep === "cartao_recusado") &&
      PAYMENT_METHOD_TARGETS[lastUserMessage]
    ) {
      nextStep = PAYMENT_METHOD_TARGETS[lastUserMessage];
    } else if (
      userStep === "gerando_pix" &&
      PIX_OPTION_TARGETS[lastUserMessage]
    ) {
      nextStep = PIX_OPTION_TARGETS[lastUserMessage];
    } else if (
      userStep === "escolha_copia_cola" &&
      lastUserMessage === "COPIA E COLA"
    ) {
      nextStep = "codigo_pix_copia_cola";
    } else if (
      userStep === "confirmacao_pagamento_pix" &&
      PIX_CONFIRMATION_TARGETS[lastUserMessage]
    ) {
      nextStep = PIX_CONFIRMATION_TARGETS[lastUserMessage];
    } else if (
      userStep === "opcoes_boleto" &&
      BOLETO_OPTION_TARGETS[lastUserMessage]
    ) {
      nextStep = BOLETO_OPTION_TARGETS[lastUserMessage];
    } else if (
      userStep === "copiar_codigo_boleto" &&
      lastUserMessage === "COPIAR CÓDIGO"
    ) {
      nextStep = "abrir_app_banco";
    } else if (
      userStep === "confirma_pagamento_banco_pix" &&
      lastUserMessage === "CONTINUAR"
    ) {
      nextStep = "confirma_pagamento_banco_novamente";
    } else if (
      userStep === "escolha_seguro" &&
      lastUserMessage === "Ótima escolha!"
    ) {
      nextStep = "termos_seguro";
    } else if (
      userStep === "escolha_seguro" &&
      lastUserMessage?.includes("seguir sem o seguro")
    ) {
      nextStep = getPostConfirmStep();
    } else if (
      userStep === "erro_geral" &&
      lastUserMessage === "TENTAR NOVAMENTE"
    ) {
      nextStep = "copy_intermediaria_2";
    } else if (
      userStep === "erro_proposta" &&
      lastUserMessage === "FAZER NOVA SIMULAÇÃO"
    ) {
      nextStep = "copy_inicial";
    } else if (
      userStep === "erro_proposta" &&
      lastUserMessage === "TENTAR NOVAMENTE"
    ) {
      nextStep = "copy_intermediaria_2";
    } else if (
      userStep === "erro_renda_insuficiente" &&
      lastUserMessage === "NÃO TENHO"
    ) {
      nextStep = "sem_renda_extra";
    } else if (
      userStep === "erro_renda_insuficiente" &&
      lastUserMessage === "TENHO"
    ) {
      nextStep = "pergunta_renda_extra";
    } else if (userStep === "pergunta_renda_extra") {
      nextStep = "renda_extra_confirmada";
    } else if (userStep === "sem_renda_extra") {
      nextStep = "carrega_planos";
    } else if (
      userStep === "renda_extra_confirmada" &&
      lastUserMessage === "Tá bem :)"
    ) {
      nextStep = "carrega_planos";
    } else if (
      userStep === "erro_sem_planos" &&
      lastUserMessage === "SIMULAR NOVAMENTE"
    ) {
      nextStep = "pergunta_valor_credito";
    } else if (
      (userStep === "planos_disponiveis" ||
        userStep === "carrega_planos") &&
      lastUserMessage === "SIMULAR NOVAMENTE"
    ) {
      nextStep = "pergunta_valor_credito";
    } else if (
      isIntermediariaCopyStep(userStep) &&
      lastUserMessage === "SIMULE AGORA MESMO"
    ) {
      nextStep = "carrega_categorias";
    } else if (
      userStep === "formulario_cartao" &&
      lastUserMessage === "VOLTAR"
    ) {
      nextStep = "alterar_forma_pagamento";
    } else if (
      userStep === "pergunta_cupom" &&
      lastUserMessage === "SIM, QUERO APLICAR"
    ) {
      nextStep = "informa_codigo_cupom";
    } else if (
      (userStep === "pergunta_cupom" ||
        userStep === "informa_codigo_cupom" ||
        userStep === "tentar_cupom_novamente") &&
      (lastUserMessage === "NÃO TENHO CUPOM" ||
        lastUserMessage === "NÃO, CONTINUAR SEM CUPOM")
    ) {
      nextStep = "aguarda_proposta";
    } else if (
      userStep === "erro_cupom_opcoes" &&
      lastUserMessage === "APLICAR UM NOVO CUPOM"
    ) {
      nextStep = "tentar_cupom_novamente";
    } else if (
      userStep === "erro_cupom_opcoes" &&
      lastUserMessage === "CONTINUAR SEM CUPOM"
    ) {
      nextStep = "aguarda_proposta";
    } else if (
      userStep === "aplica_cupom_url" &&
      lastUserMessage === "SIM, QUERO APLICAR"
    ) {
      nextStep = "informa_codigo_cupom";
    } else if (
      userStep === "aplica_cupom_url" &&
      lastUserMessage === "NÃO QUERO USAR O CUPOM"
    ) {
      nextStep = "aguarda_proposta";
    } else if (
      userStep === "mensagem_pagamento_cupom" &&
      lastUserMessage === "NÃO QUERO USAR O CUPOM"
    ) {
      nextStep = "aguarda_proposta";
    } else if (
      userStep === "comprar_outra_cota" &&
      lastUserMessage === "SIM"
    ) {
      nextStep = "pergunta_mesmo_credito";
    } else if (
      userStep === "comprar_outra_cota" &&
      lastUserMessage === "NÃO"
    ) {
      nextStep = "inicio_pesquisa";
    } else if (
      userStep === "pergunta_mesmo_credito" &&
      lastUserMessage === "SIM"
    ) {
      nextStep = "revisao_dados_pessoais";
    } else if (
      userStep === "pergunta_mesmo_credito" &&
      lastUserMessage === "NÃO"
    ) {
      nextStep = "carrega_categorias";
    } else if (lastUserMessage === "CONTINUAR" && userStep === "proxima_assembleia") {
      nextStep = "compra_finalizada";
    } else if (lastUserMessage === "NÃO" && userStep === "compra_finalizada") {
      nextStep = "inicio_pesquisa";
    } else if (lastUserMessage === "SIM" && userStep === "compra_finalizada") {
      nextStep = "pergunta_mesmo_credito";
    } else if (userStep === "pergunta_conhecer_mycon") {
      nextStep = "pergunta_avaliacao_mycon";
    } else if (userStep === "pergunta_avaliacao_mycon" && /^[1-5]$/.test(lastUserMessage)) {
      nextStep = "pergunta_ajuda_especialista";
    } else if (lastUserMessage === "SIM" && userStep === "pergunta_ajuda_especialista") {
      nextStep = "pergunta_nota_especialista";
    } else if (lastUserMessage === "NÃO" && userStep === "pergunta_ajuda_especialista") {
      nextStep = "pesquisa_finalizada";
    } else if (
      userStep === "pergunta_nota_especialista" &&
      /^[1-5]$/.test(lastUserMessage)
    ) {
      nextStep =
        lastUserMessage === "1"
          ? "pergunta_comentario_especialista"
          : "pesquisa_finalizada";
    } else if (userStep === "pergunta_comentario_especialista") {
      nextStep = "pesquisa_finalizada";
    } else if (
      lastUserMessage === "PAGAR NO CARTÃO" &&
      userStep === "formulario_cartao"
    ) {
      const cardOutcome = [...historico]
        .reverse()
        .find(
          (e) => !META_STEPS.includes(e.step) && CARD_PAYMENT_OUTCOME_STEPS.has(e.step)
        );
      if (cardOutcome) {
        nextStep = cardOutcome.step;
      }
    }
  }

  if (lastEntry.step === "processando_cartao") {
    const cardOutcome = [...historico]
      .reverse()
      .find(
        (e) => !META_STEPS.includes(e.step) && CARD_PAYMENT_OUTCOME_STEPS.has(e.step)
      );
    if (cardOutcome) {
      return cardOutcome.step;
    }
    return "processando_cartao";
  }

  const cepResume = getCepFlowResumeStep(historico, lastEntry);
  if (cepResume) {
    return cepResume;
  }

  if (
    lastEntry.step === "informa_codigo_cupom" &&
    userAnswerEntry &&
    historico.some((e) => e.step.startsWith("cupom-error"))
  ) {
    return "erro_cupom_opcoes";
  }

  if (POST_PROPOSTA_STEPS.has(lastEntry.step)) {
    const hasPayment = historico.some(
      (e) => e.step === "inicio_pagamento" && e.sender === "bot"
    );
    if (hasPayment) {
      return "inicio_pagamento";
    }
  }

  const pix = useUserStore.getState().payment?.pix;
  const hasPixQrcodeData = hasPixQrcodeInStore();
  const hasPixCode = !!pix?.qrCode;

  const orphanPaymentStep = getResumeStepAfterOrphanPaymentTail(sorted);
  if (orphanPaymentStep) {
    return orphanPaymentStep;
  }

  const pendingInteractiveStep = getPendingInteractiveBotStep(
    sorted,
    PIX_DEFERRED_INTERACTIVE_STEPS
  );
  if (pendingInteractiveStep) {
    return pendingInteractiveStep;
  }

  const { method: pixMethod, pixOption } = getPaymentContext(historico);

  if (
    hasPixCode &&
    (PIX_COPY_PASTE_RENDER_STEPS.has(lastEntry.step) ||
      nextStep === "codigo_pix_copia_cola" ||
      pixOption === "copy-paste")
  ) {
    const codeAlreadyShown = sorted.some(
      (e) => e.step === "exibe_codigo_pix" && e.sender === "bot"
    );
    return codeAlreadyShown
      ? "botao_copiar_pix"
      : "exibe_codigo_pix";
  }

  if (
    hasPixQrcodeData &&
    (PIX_QRCODE_RENDER_STEPS.has(lastEntry.step) ||
      nextStep === "mensagem_qrcode_pix" ||
      pixOption === "qrcode")
  ) {
    return "qrcode_pix";
  }

  const stoppedAtSharedTail =
    SHARED_PAYMENT_TAIL.has(lastEntry.step) &&
    !(lastUserMessage === "CONTINUAR" && lastEntry.step === "proxima_assembleia");

  if (stoppedAtSharedTail) {
    if (pixMethod === "pix" && pixOption === "qrcode" && hasPixQrcodeData) {
      return "qrcode_pix";
    }
    if (pixMethod === "pix" && pixOption === "copy-paste" && hasPixCode) {
      const codeAlreadyShown = sorted.some(
        (e) => e.step === "exibe_codigo_pix" && e.sender === "bot"
      );
      return codeAlreadyShown
        ? "botao_copiar_pix"
        : "exibe_codigo_pix";
    }
  }

  if (lastEntry.step === "mensagem_carrossel_planos" && userAnswerEntry) {
    const savedPlano = useConsorcioStore.getState().selectedPlano;
    if (savedPlano?.planoID) {
      return "confirmacao_informacoes";
    }
  }

  const proposalAlreadyCreated = historico.some(
    (e) => e.step === "aguarda_registro_dados" && e.sender === "bot"
  );

  if (
    !proposalAlreadyCreated &&
    (STEPS_BEFORE_PROPOSAL_CREATION.has(lastEntry.step) ||
      STEPS_BEFORE_PROPOSAL_CREATION.has(nextStep))
  ) {
    const selectedPlano = useConsorcioStore.getState().selectedPlano;
    if (!selectedPlano?.planoID) {
      return "carrega_planos";
    }
  }

  if (
    lastEntry.sender === "bot" &&
    INTERACTIVE_STEPS.has(lastEntry.step) &&
    !userAnswerEntry
  ) {
    return lastEntry.step;
  }

  return nextStep;
};

const resumeConversationById = async (conversacaoId) => {
  if (!conversacaoId) return false;

  localStorage.setItem(LOCAL_STORAGE_CONVERSACAO_KEY, conversacaoId);

  const conversacao = await recuperarConversacao(conversacaoId);
  if (!conversacao) {
    clearResumeStorage();
    goToCopyInicial();
    return false;
  }

  if (hasSessionConfigMismatch(conversacao)) {
    await startFreshSimulation();
    return false;
  }

  const mensagens = await recuperarMensagens(conversacaoId);
  const historico = mensagens ?? [];

  const hasUserInteractions = historico.some(
    (entry) => entry.sender === "user" && !META_STEPS.includes(entry.step)
  );

  if (!hasUserInteractions) {
    useBotControlStore.setState({ currentStep: findStep("copy_inicial").index });
    return true;
  }

  const leadId =
    conversacao.leadId ||
    localStorage.getItem(LOCAL_STORAGE_LEAD_KEY) ||
    "";

  useBotControlStore.getState().clearPendingMessageBlock();
  restoreStoreFromHistorico(historico, leadId);

  const resumeStepName = getResumeStep(historico);
  const restoredMessages = buildMessagesFromHistorico(historico, resumeStepName);

  if (restoredMessages.length > 0) {
    useBotControlStore.setState({ messages: restoredMessages });
  }

  removeLoadingMessage();
  useBotControlStore.setState({ isBotLoading: false });

  const refetchingPlans = ensurePlansLoadedForResume(resumeStepName, historico);
  const refetchingCategories = await ensureCategoriesLoadedForResume(
    resumeStepName,
    historico
  );

  if (!refetchingPlans && !refetchingCategories) {
    useBotControlStore.setState({
      skipStepTracking: true,
      skipStepAction: RESUME_SKIP_ACTION_STEPS.has(resumeStepName),
      currentStep: findStep(resumeStepName).index,
    });
  }

  return true;
};

let checkResumeExecution = null;

const runCheckResume = async () => {
  const urlConversacaoId = getConversacaoIdFromUrl();
  const localConversacaoId = localStorage.getItem(LOCAL_STORAGE_CONVERSACAO_KEY);
  const conversacaoId = urlConversacaoId || localConversacaoId;

  if (urlConversacaoId && shouldAutoResumeFromUrl()) {
    await resumeConversationById(urlConversacaoId);
    return;
  }

  if (urlConversacaoId) {
    localStorage.setItem(LOCAL_STORAGE_CONVERSACAO_KEY, urlConversacaoId);
  }

  if (!conversacaoId) {
    goToCopyInicial();
    return;
  }

  const conversacao = await recuperarConversacao(conversacaoId);

  if (!conversacao) {
    clearResumeStorage();
    goToCopyInicial();
    return;
  }

  if (hasSessionConfigMismatch(conversacao)) {
    await startFreshSimulation();
    return;
  }

  const mensagens = await recuperarMensagens(conversacaoId);
  const hasUserInteractions = (mensagens ?? []).some(
    (e) => e.sender === "user" && !META_STEPS.includes(e.step)
  );

  if (!hasUserInteractions) {
    useBotControlStore.setState({ currentStep: findStep("copy_inicial").index });
    return;
  }

  useBotControlStore.setState({ currentStep: findStep("retomar_conversa").index });
};

const resumeSteps = [
  {
    name: "verifica_retomada",
    sender: "bot",
    text: "",
    isWaitingForFetch: true,
    action: async () => {
      if (!checkResumeExecution) {
        checkResumeExecution = runCheckResume();
      }

      await checkResumeExecution;
    },
  },
  {
    name: "retomar_conversa",
    sender: "bot",
    text: "Olá! Vimos que você iniciou uma simulação. Deseja continuar de onde parou ou iniciar uma nova?",
    options: [
      {
        text: "CONTINUAR DE ONDE PAREI",
        action: async () => {
          const conversacaoId =
            localStorage.getItem(LOCAL_STORAGE_CONVERSACAO_KEY) ||
            getConversacaoIdFromUrl();

          await resumeConversationById(conversacaoId);
        },
      },
      {
        text: "INICIAR NOVA SIMULAÇÃO",
        action: async () => {
          clearResumeStorage();
          useConversacaoStore.getState().setConversacao({
            id: "", stage: "", metadata: {}, historico: [],
          });
          goToCopyInicial();
        },
      },
    ],
  },
];

export {
  LOCAL_STORAGE_LEAD_KEY,
  LOCAL_STORAGE_CONVERSACAO_KEY,
  RESUME_SKIP_ACTION_STEPS,
  getResumeStep,
  getUserAnswerEntry,
  buildMessagesFromHistorico,
  parseCreditRangesFromMessage,
  restoreRangesFromHistorico,
  resumeConversationById,
};
export default resumeSteps;
